// deps
import React, { ReactElement, useCallback, useState } from "react";

// internal-deps
import { NativeBridge } from "@swiggy-private/native-bridge";
import { ErrorBoundary, isEmpty, useEffectOnce } from "@swiggy-private/core-ui";
import { PAYLATER_TYPES } from "@swiggy-private/payment-types";
import Blaze from "@swiggy-private/blaze.js";
import {
    SwgyHeadersKeys,
    CheckoutState,
    CheckoutActionTypes,
    useInit,
    recordNREvent,
    BLAZE_EVENTS,
    recordWebviewRefreshState,
    restoreWebviewRefreshState,
    NEWRELIC_TABLES,
    getBridgeSdk,
    CheckoutRoute,
    scheduleIdle,
} from "@swiggy-private/checkout-core";
import { getNRErrorInfo } from "@swiggy-private/js-utils";

// helpers
import { isWebCartFlow } from "@checkout-main/utils/WebCartFlow";

// components
import { ContextProvider } from "@checkout-main/components/context-provider";
import { CheckoutRouter } from "@checkout-main/components/checkout-router";

// ---------------------------------------------------------------------------
// Food-cart asset prefetch.
//
// These assets power the "transition" screen shown between the cart and certain
// downstream flows (tip selection, address pickers). Previously they were fetched
// from an inline <script> tag in server/views/script.html during HTML parse. Chrome
// treats `fetch()` calls with no priority hint as High priority by default, so those
// requests raced the main JS entry chunk for network slots in the critical window.
//
// They are now fired from inside `useEffectOnce` after the `checkout_app_load_time`
// metric is captured, wrapped in `scheduleIdle`, and carry an explicit
// `priority: "low"` hint so browsers that support Priority Hints (Chrome 101+,
// Safari 17.2+) deprioritize them further. Older browsers ignore the hint.
//
// Invocation is gated on food-cart entry routes to preserve the previous
// `{% if isFoodCheckoutApp %}` server-template behaviour — the dineout and payment
// flows never rendered this transition screen.
// ---------------------------------------------------------------------------
const FOOD_CART_TRANSITION_PREFETCH_URLS: ReadonlyArray<string> = [
    "https://media-assets.swiggy.com/swiggy/image/upload/fl_lossy,f_auto,q_auto/portal/m/cw_transition_screen_v2/images/img_0.png",
    "https://media-assets.swiggy.com/swiggy/image/upload/fl_lossy,f_auto,q_auto/portal/m/cw_transition_screen_v2/images/img_1.png",
    "https://media-assets.swiggy.com/swiggy/image/upload/fl_lossy,f_auto,q_auto/portal/m/cw_transition_screen_v2/images/work.png",
    "https://media-assets.swiggy.com/swiggy/image/upload/fl_lossy,f_auto,q_auto/portal/m/cw_transition_screen_v2/images/family_friends.png",
    "https://media-assets.swiggy.com/swiggy/image/upload/fl_lossy,f_auto,q_auto/portal/m/cw_transition_screen_v2/images/others.png",
    "https://media-assets.swiggy.com/swiggy/raw/upload/portal/m/cw_transition_screen_v2/transition_loader.json",
    "https://media-assets.swiggy.com/swiggy/raw/upload/portal/m/cw_de_tip_lottie/pre_tip_add_lottie.json",
];

// Gifting cart is deliberately excluded. Its tick-screen uses a different base lottie
// (`TRANSITION_LOTTIE_GIFTING_ORDER_PLACED_URL`, prefetched separately in
// `usePrefetchTickScreenLottie`), so the `transition_loader.json` in the list below is
// dead weight on that flow. The previous inline prefetch ran on gifting too only because
// the old template-level gate (`{% if isFoodCheckoutApp %}`) was broader than needed.
const FOOD_CART_ROUTES: ReadonlyArray<CheckoutRoute> = [
    CheckoutRoute.CART,
    CheckoutRoute.TOING_CART,
    CheckoutRoute.SHARED_CART,
];

// TypeScript 3.9 does not yet include `priority` on `RequestInit`. The cast is
// required until we bump TS; Chromium-based browsers and recent Safari honour it,
// all others silently ignore the unknown property.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const LOW_PRIORITY_FETCH_INIT: RequestInit = { priority: "low" } as any;

const prefetchFoodCartAssets = (): void => {
    if (typeof fetch === "undefined") return;
    // URLs are hardcoded string literals and the init is a module-level constant, so the
    // synchronous call cannot throw — the only failure mode is an async rejection, which
    // `.catch` swallows.
    FOOD_CART_TRANSITION_PREFETCH_URLS.forEach((url) => {
        fetch(url, LOW_PRIORITY_FETCH_INIT).catch(() => void 0);
    });
};

// types
import { CHECKOUT_INITIAL_STATE, InitialCheckoutRoute } from "@checkout-main/interfaces/index";
import { getInitialCheckoutRoute } from "@checkout-main/helpers/index";

export interface CheckoutMainProps {
    config: {
        apiHost: string;
        paymentHost: string;
        analyticsHost: string;
        paymentReturnUrl?: string;
        addMoneyReturnUrl?: string;
        addSwiggyMoneyReturnUrl?: string;
        cachePaymentData?: boolean;
        newrelicEndpoint?: string;
        newrelicKey?: string;
        environment?: string;
        simplPostLinkingRedirectUrl: string;
        enableWebviewRefreshCapability?: boolean;
    };
    headers: { [key in Partial<SwgyHeadersKeys>]: string };
    initialState?: {
        state?: CHECKOUT_INITIAL_STATE;
        routeName?: CheckoutRoute;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        routeData?: any;
    };
    nativeBridgeSdk?: NativeBridge;
}

export function CheckoutMain(props: CheckoutMainProps): ReactElement {
    const { config, headers, initialState: checkoutState = {} } = props;

    const [initialRoute] = useState<InitialCheckoutRoute>(() => {
        if (checkoutState.state === CHECKOUT_INITIAL_STATE.CONFIRM_ORDER) {
            // added condition to push extra state in case of cart web view
            if (isWebCartFlow()) {
                window.history.pushState(
                    {
                        name: CheckoutRoute.PAYMENTS,
                        data: null,
                    },
                    "",
                    "",
                );
            }
            return {
                name: CheckoutRoute.CONFIRM_ORDER,
            };
        }

        if (checkoutState.state === CHECKOUT_INITIAL_STATE.DIRECT_PLACE_ORDER) {
            return { name: CheckoutRoute.DIRECT_PLACE_ORDER };
        }

        if (
            checkoutState.state &&
            [
                CHECKOUT_INITIAL_STATE.PAYLATER_SIMPL_LINKING_FAILED,
                CHECKOUT_INITIAL_STATE.PAYLATER_SIMPL_LINKING_SUCCESS_DONT_PROCEED_WITH_ORDER,
                CHECKOUT_INITIAL_STATE.PAYLATER_SIMPL_LINKING_SUCCESS_PROCEED_WITH_ORDER,
                CHECKOUT_INITIAL_STATE.PAYMENTS,
                CHECKOUT_INITIAL_STATE.WALLET_MONEY_RECEIVED,
            ].includes(checkoutState.state)
        ) {
            return { name: CheckoutRoute.PAYMENTS };
        }

        if (checkoutState.state === CHECKOUT_INITIAL_STATE.CART) {
            return getInitialCheckoutRoute(headers);
        }

        if (checkoutState.state === CHECKOUT_INITIAL_STATE.SHARED_CART) {
            return { name: CheckoutRoute.SHARED_CART };
        }

        if (checkoutState.state === CHECKOUT_INITIAL_STATE.GIFTING_CART) {
            return { name: CheckoutRoute.GIFTING_CART };
        }

        if (checkoutState.routeName) {
            return { name: checkoutState.routeName, data: checkoutState.routeData };
        }

        return getInitialCheckoutRoute(headers);
    });

    const [initialState] = useState<CheckoutState>(
        (): CheckoutState => {
            // Checking if user webview refresh happened and redirecting user to its last interactive state
            const restoredWebviewRefreshState = restoreWebviewRefreshState(config?.enableWebviewRefreshCapability);
            try {
                // TODO: remove this temporary debug log once debugging is concluded for IM duplicate order issue
                recordNREvent({
                    eventType: NEWRELIC_TABLES.WEBVIEW_REFRESH_DEBUG,
                    name: "restoreWebviewRefreshStateInitialState",
                    isUserLastStateNonEmpty: String(!isEmpty(restoredWebviewRefreshState)),
                });
            } catch {
                /* No-op */
            }
            if (!isEmpty(restoredWebviewRefreshState)) {
                return restoredWebviewRefreshState;
            }

            if (checkoutState.state === CHECKOUT_INITIAL_STATE.WALLET_MONEY_RECEIVED) {
                // added condition to push extra state in case of cart web view
                if (isWebCartFlow()) {
                    window.history.pushState({ name: CheckoutRoute.PAYMENTS, data: null }, "", "");
                }
                return { type: CheckoutActionTypes.ADD_MONEY_CONFIRM };
            }

            if (checkoutState.state === CHECKOUT_INITIAL_STATE.DIRECT_PLACE_ORDER) {
                return { type: CheckoutActionTypes.DIRECT_PLACE_ORDER };
            }

            if (checkoutState.state === CHECKOUT_INITIAL_STATE.PAYLATER_SIMPL_LINKING_FAILED) {
                return {
                    type: CheckoutActionTypes.PAYLATER_SIMPL_LINKING_FAILED,
                    payload: {
                        error: true,
                        paymentCode: PAYLATER_TYPES.SIMPL,
                    },
                };
            }
            if (checkoutState.state === CHECKOUT_INITIAL_STATE.PAYLATER_SIMPL_LINKING_SUCCESS_DONT_PROCEED_WITH_ORDER) {
                return {
                    type: CheckoutActionTypes.PAYLATER_SIMPL_LINKING_FAILED,
                    payload: {
                        paymentCode: PAYLATER_TYPES.SIMPL,
                    },
                };
            }

            if (checkoutState.state === CHECKOUT_INITIAL_STATE.PAYLATER_SIMPL_LINKING_SUCCESS_PROCEED_WITH_ORDER) {
                return {
                    type: CheckoutActionTypes.PAYLATER_SIMPL_LINKING_SUCCESS_PROCEED_WITH_ORDER,
                    payload: {
                        paymentCode: PAYLATER_TYPES.SIMPL,
                    },
                };
            }

            if (checkoutState.state === CHECKOUT_INITIAL_STATE.PAYMENTS) {
                return { type: CheckoutActionTypes.SHOW_PAYMENTS };
            }

            if (checkoutState.state === CHECKOUT_INITIAL_STATE.CONFIRM_ORDER) {
                return {
                    type: CheckoutActionTypes.CONFIRM_ORDER,
                    payload: null,
                };
            }

            if (checkoutState.state === CHECKOUT_INITIAL_STATE.SHARED_CART) {
                return {
                    type: CheckoutActionTypes.SHARED_CART,
                    payload: { groupCartId: checkoutState?.routeData?.groupCartId || "" },
                };
            }

            if (checkoutState.state === CHECKOUT_INITIAL_STATE.GIFTING_CART) {
                return {
                    type: CheckoutActionTypes.SHOW_GIFTING_CART,
                    payload: null,
                };
            }

            return { type: CheckoutActionTypes.SHOW_CART, payload: null };
        },
    );

    useInit({
        headers,
        nbSdk: props.nativeBridgeSdk,
        newrelicEndpoint: config.newrelicEndpoint,
        newrelicKey: config.newrelicKey,
        environment: config.environment,
    });

    useEffectOnce(() => {
        // Order matters here. `Blaze.recordTimeFromAppStart` is the end-marker for the
        // `checkout_app_load_time` metric, so any synchronous work that runs before it
        // inflates the number we are optimizing. Previously this call sat BELOW the bridge
        // SDK and NR-event calls, which added ~10-20ms of bookkeeping to every recorded
        // sample. Moving it to the top of the effect makes the metric reflect app readiness
        // instead of app-readiness-plus-telemetry-overhead.
        Blaze.recordTimeFromAppStart(BLAZE_EVENTS.RECORD_APP_LOAD_TIME, { initialState: checkoutState.state || "--" });

        // Start-markers for downstream Blaze metrics need to anchor at the same moment the
        // corresponding end-marker will later be recorded, so they stay synchronous with the
        // metric above. Deferring them would shift the measured duration.
        switch (initialRoute.name) {
            case CheckoutRoute.CART:
            case CheckoutRoute.TOING_CART:
                Blaze.recordStart(BLAZE_EVENTS.RECORD_CART_LOAD_TIME);
                break;
            case CheckoutRoute.SHARED_CART:
                Blaze.recordStart(BLAZE_EVENTS.RECORD_INTERMEDIATE_SHARED_CART_SCREEN_LOAD_TIME);
                break;
        }

        // Everything below is either pure bookkeeping (NR events, perf-mark reporting) or
        // low-priority prefetching. None of it blocks any UI or is read later in this page's
        // lifecycle, so it's safe to push to idle time. The 1.5s timeout guarantees the
        // callback runs even under heavy main-thread load.
        scheduleIdle(
            () => {
                // Re-enable screen capture via the native bridge. Fire-and-forget; the bridge
                // itself is asynchronous.
                getBridgeSdk()
                    .setScreenCaptureState({ screenCapturingDisabled: false })
                    .catch(() => void 0);

                // NR session-tracking event. The two event types are mutually exclusive based
                // on the entry route.
                if (initialRoute.name === CheckoutRoute.SHARED_CART) {
                    recordNREvent({
                        eventType: NEWRELIC_TABLES.GROUP_CART_WEB_VIEW,
                        releaseVersion: window.__RELEASE_VERSION__ || "--",
                    });
                } else {
                    recordNREvent({
                        eventType: NEWRELIC_TABLES.CHECKOUT_WEB_VIEW,
                        releaseVersion: window.__RELEASE_VERSION__ || "--",
                    });
                }

                // Telemetry only — despite the name, this function does NOT persist any
                // state. It reads the native webview's last-state payload (maintained on the
                // native side via `window.__webview_state__.getWebViewLastState()`) and emits
                // NR events (`WEBVIEW_REFRESH_DEBUG`, `WEBVIEW_REFRESH_STATE`) describing it.
                // No localStorage / sessionStorage writes happen here. The actual recovery
                // read is `restoreWebviewRefreshState`, which already ran synchronously in
                // the `useState` initializer above. Safe to defer — worst case on fast unload
                // is a dropped observability event, not a correctness issue.
                recordWebviewRefreshState(config?.enableWebviewRefreshCapability);

                // Report granular performance marks to NR. Pure observability — the marks
                // themselves were written synchronously by the entry bundle; we just collect
                // and forward them here.
                try {
                    const markNames = [
                        "checkout_bootstrap_start",
                        "checkout_bootstrap_end",
                        "checkout_hydration_start",
                        "checkout_hydration_end",
                    ];
                    const markData: Record<string, number> = {};
                    markNames.forEach((name) => {
                        const entries = performance.getEntriesByName(name, "mark");
                        if (entries.length > 0) {
                            markData[name] = Math.round(entries[0].startTime);
                        }
                    });

                    if (Object.keys(markData).length > 0) {
                        const durations: Record<string, number> = {};
                        if (
                            markData.checkout_bootstrap_start !== undefined &&
                            markData.checkout_bootstrap_end !== undefined
                        ) {
                            durations.bootstrap_duration_seconds =
                                (markData.checkout_bootstrap_end - markData.checkout_bootstrap_start) / 1000;
                        }
                        if (
                            markData.checkout_hydration_start !== undefined &&
                            markData.checkout_hydration_end !== undefined
                        ) {
                            durations.hydration_duration_seconds =
                                (markData.checkout_hydration_end - markData.checkout_hydration_start) / 1000;
                        }

                        recordNREvent({
                            eventType: NEWRELIC_TABLES.CHECKOUT_WEB_PERF_MARKS,
                            ...markData,
                            ...durations,
                        });
                    }
                } catch (_) {
                    /* noop */
                }

                // Food-cart-only prefetch: warm up transition-screen assets for downstream
                // screens so they render without a network wait when the user lands there.
                if (FOOD_CART_ROUTES.indexOf(initialRoute.name) !== -1) {
                    prefetchFoodCartAssets();
                }
            },
            { timeout: 1500 },
        );
    });

    const onError = useCallback((error?: Error, errorInfo?: React.ErrorInfo) => {
        const errorEvents = getNRErrorInfo(error, errorInfo);

        recordNREvent({
            eventType: NEWRELIC_TABLES.CHECKOUT_WEB_ERROR_BOUNDARY,
            ...errorEvents,
        });
    }, []);

    return (
        <ErrorBoundary errorFallback={onError}>
            <ContextProvider config={config} initialState={initialState} headers={headers}>
                <CheckoutRouter initialRoute={initialRoute} />
            </ContextProvider>
        </ErrorBoundary>
    );
}
